/**
 * Reusable helpers for the admin test modules: formatting, search text, API calls, CSV export.
 */

import authService from "../../../utils/authService";
import {
  ASSESSMENT_STATUS,
  REPORT_STUDENT_STATUS,
  isFinalLikeTestType,
  type ReportStudentDisplayStatus,
} from "../../../constants/testConstants";
import { TEST_FLOW_API } from "./testApiPaths";
import { compareMaybeNumeric, compareNumericScore, isScoreSortField } from "../../../utils/numericSortUtils";
import type {
  AssignTestsFilter,
  AssignedBatchOption,
  BatchConfigEntry,
  ReportData,
  ReportStudentRow,
  ReportTestsFilter,
  SelectedSubject,
  TestData,
  TestFlowAssignmentItem,
  TestReportViewData,
  TestSecurityConfiguration,
  TestSecurityConfigurationPayload,
  LiveProctoringFeed,
  TestTypeViolationPolicyListResponse,
  TestTypeViolationPolicyUpdatePayload,
  TestTypeViolationPolicyUpdateResponse,
} from "./testTypes";

/** Format API date as DD-M-YY for card display */
export const formatDate = (dateStr: string | null) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  return `${date.getDate()}-${date.getMonth() + 1}-${date
    .getFullYear()
    .toString()
    .slice(-2)}`;
};

/** Format 24h time string as 12h AM/PM for card display */
export const formatTime = (timeStr: string | null) => {
  if (!timeStr) return null;
  const [h, m] = timeStr.split(":");
  let hour = parseInt(h, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  hour = hour % 12 || 12;
  return `${hour.toString().padStart(2, "0")}:${m} ${ampm}`;
};

const formatReportTimePart = (timeStr: string) => {
  const trimmed = timeStr.trim();
  if (!trimmed) return "";
  return /am|pm/i.test(trimmed) ? trimmed : formatTime(trimmed) ?? trimmed;
};

/** Date line for report summary cards */
export const getReportCardDate = (report: ReportData): string | null => {
  if (report.date) return formatDate(report.date);
  for (const value of [report.from_time, report.end_time]) {
    if (value?.includes("T")) return formatDate(value);
  }
  return null;
};

/** Time range for report summary cards (from_time/end_time or legacy time field) */
export const formatReportTimeRange = (report: ReportData): string | null => {
  const from = report.from_time?.trim();
  const end = report.end_time?.trim();
  if (from || end) {
    return [from, end]
      .filter((t): t is string => Boolean(t?.trim()))
      .map(formatReportTimePart)
      .join(" - ");
  }
  const combined = report.time?.trim();
  if (!combined) return null;
  if (/am|pm/i.test(combined)) return combined;
  return combined
    .split(/\s*-\s*/)
    .map((part) => formatReportTimePart(part))
    .join(" - ");
};

/** Case-insensitive match against visible card text */
export const matchesVisibleCardSearch = (
  visibleText: string,
  cardSearchQuery: string
) => {
  if (!cardSearchQuery) return true;
  return visibleText.toLowerCase().includes(cardSearchQuery.toLowerCase());
};

/** Build searchable string from assign-tab test card fields */
export const getVisibleTestSearchText = (test: TestData): string => {
  const durationLabel = test.duration === "1" ? "Minute" : "Minutes";
  return [
    test.title,
    test.subjects?.length ? test.subjects.join(" ") : test.subject,
    test.batch,
    test.description,
    test.duration,
    durationLabel,
    test.marks != null ? String(test.marks) : "",
    "Marks",
    test.date ? formatDate(test.date) : "",
    test.time ? formatTime(test.time) : "",
  ]
    .filter((v) => v != null && v !== "")
    .join(" ");
};

/** Build searchable string from report-tab card fields */
export const getVisibleReportSearchText = (report: ReportData): string => {
  const durationLabel = parseInt(report.duration) === 1 ? "Minute" : "Minutes";
  return [
    report.title,
    report.subject,
    report.batch,
    report.description,
    report.duration,
    durationLabel,
    report.marks != null ? String(report.marks) : "",
    "Marks",
    report.invited != null ? String(report.invited) : "",
    "invited",
    getReportCardDate(report) ?? "",
    formatReportTimeRange(report) ?? "",
  ]
    .filter((v) => v != null && v !== "")
    .join(" ");
};

/** Newest assigned date first; cards with no date go last */
export const sortReportsByAssignedDateDesc = (
  list: ReportData[]
): ReportData[] =>
  [...list].sort((a, b) => {
    if (!a.date && !b.date) return 0;
    if (!a.date) return 1;
    if (!b.date) return -1;
    return b.date.localeCompare(a.date);
  });

/** Proctoring flags are fixed in the Electron app; persisted as enabled on save. */
export const DEFAULT_TEST_SECURITY_CONFIGURATION: TestSecurityConfigurationPayload =
  {
    enable_proctoring: true,
    require_electron_app: true,
    require_fullscreen: true,
    enable_event_tracking: true,
    track_copy_paste: true,
    track_tab_changes: true,
    track_application_switching: true,
    block_screen_sharing_apps: true,
    block_remote_access_tools: true,
    block_recording_applications: true,
    auto_release_test_report: true,
  };

export const pickTestSecurityConfigurationPayload = (
  config: Partial<TestSecurityConfiguration> | null | undefined
): TestSecurityConfigurationPayload => {
  const payload = { ...DEFAULT_TEST_SECURITY_CONFIGURATION };
  if (
    config &&
    typeof config.auto_release_test_report === "boolean"
  ) {
    payload.auto_release_test_report = config.auto_release_test_report;
  }
  return payload;
};

/** Build API payload: proctoring always on, only auto-release is configurable. */
export const buildTestSecuritySavePayload = (
  autoRelease: boolean,
  batchId: string
): TestSecurityConfigurationPayload => ({
  ...DEFAULT_TEST_SECURITY_CONFIGURATION,
  auto_release_test_report: autoRelease,
  batch_id: batchId,
});

const normalizeApiString = (value: unknown): string | null => {
  if (value == null || value === "") return null;
  return String(value);
};

/** Map for-assign API row to TestData for configure cards */
export const mapTestDataFromApi = (row: Record<string, unknown>): TestData => ({
  test_id: String(row.test_id ?? ""),
  title: String(row.title ?? ""),
  description: String(row.description ?? ""),
  duration: String(row.duration ?? ""),
  marks: Number(row.marks ?? 0),
  subject: normalizeApiString(row.subject),
  subjects: Array.isArray(row.subjects)
    ? row.subjects.map(String)
    : row.subject
      ? [String(row.subject)]
      : undefined,
  subject_ids: Array.isArray(row.subject_ids)
    ? row.subject_ids.map(String)
    : undefined,
  date: normalizeApiString(row.date),
  time: normalizeApiString(row.time),
  track_id: normalizeApiString(row.track_id),
  course_id: normalizeApiString(row.course_id),
  batch_id: normalizeApiString(row.batch_id),
  batch: normalizeApiString(row.batch),
  test_type: normalizeApiString(row.test_type),
});

/** Map report-list API row to ReportData */
export const mapReportDataFromApi = (row: Record<string, unknown>): ReportData => ({
  status: String(row.status ?? ""),
  test_id: String(row.test_id ?? ""),
  title: String(row.title ?? ""),
  description: String(row.description ?? ""),
  duration: String(row.duration ?? ""),
  marks: Number(row.marks ?? 0),
  subject: normalizeApiString(row.subject),
  date: normalizeApiString(row.date),
  time:
    normalizeApiString(row.time) ??
    (([row.from_time, row.end_time]
      .filter((part) => part != null && String(part).trim())
      .map(String)
      .join(" - ") ||
      null) as string | null),
  track: normalizeApiString(row.track),
  course: normalizeApiString(row.course),
  test_type: normalizeApiString(row.test_type),
  invited: Number(row.invited ?? 0),
  from_time: normalizeApiString(row.from_time),
  end_time: normalizeApiString(row.end_time),
  batch: normalizeApiString(row.batch),
});

/** Fetch security/proctoring configuration for a test */
export const fetchTestSecurityConfiguration = async (
  testId: string,
  batchId?: string | null
): Promise<TestSecurityConfiguration> => {
  const query = batchId
    ? `?batch_id=${encodeURIComponent(batchId)}`
    : "";
  const response = await authService.get(
    `${TEST_FLOW_API}/tests/${testId}/security-configuration${query}`
  );
  return response.data;
};

/** Manually release or withhold student test reports */
export const releaseTestReports = async (
  testId: string,
  studentIds: string[],
  release = true,
  slotIds: number[] = []
) => {
  const response = await authService.post(
    `${TEST_FLOW_API}/tests/${testId}/report/release`,
    { student_ids: studentIds, slot_ids: slotIds, release }
  );
  return response.data;
};

/** Save security/proctoring configuration for a test */
export const saveTestSecurityConfiguration = async (
  testId: string,
  payload: TestSecurityConfigurationPayload
): Promise<TestSecurityConfiguration> => {
  const response = await authService.post(
    `${TEST_FLOW_API}/tests/${testId}/security-configuration`,
    payload
  );
  return response.data;
};

/** Fetch student-wise exam violations for admin report / proctoring review */
export const fetchStudentProctoringViolations = async (
  testId: string,
  studentId: string
) => {
  const response = await authService.get(
    `${TEST_FLOW_API}/tests/${testId}/proctoring/students/${studentId}`
  );
  return response.data;
};

/** Live proctoring feed — poll with `since` = previous latest_event_at */
export const fetchLiveProctoringFeed = async (
  testId: string,
  since?: string | null,
  limit = 100
): Promise<LiveProctoringFeed> => {
  const params = new URLSearchParams({ limit: String(limit) });
  if (since) params.set("since", since);
  const response = await authService.get(
    `${TEST_FLOW_API}/tests/${testId}/proctoring/live?${params.toString()}`
  );
  return response.data;
};

/** Fetch configurable violation policies per test type */
export const fetchViolationPolicies =
  async (): Promise<TestTypeViolationPolicyListResponse> => {
    const response = await authService.get(
      `${TEST_FLOW_API}/violation-policies`
    );
    return response.data;
  };

/** Update one violation policy by test type */
export const updateViolationPolicy = async (
  testType: string,
  payload: TestTypeViolationPolicyUpdatePayload
): Promise<TestTypeViolationPolicyUpdateResponse> => {
  const response = await authService.put(
    `${TEST_FLOW_API}/violation-policies/${encodeURIComponent(testType)}`,
    payload
  );
  return response.data;
};

export type { ReportStudentDisplayStatus };

type ReportStudentStatusInput = {
  Test_status: string;
  display_status?: string;
  session_status?: string | null;
  test_start_time?: string;
  violation_count?: number;
};

/** Map live proctoring session_status to admin display label. */
export const resolveLiveProctoringSessionStatus = (
  sessionStatus?: string | null
): ReportStudentDisplayStatus => {
  const raw = (sessionStatus ?? "").trim().toLowerCase();
  if (raw === "violated") return REPORT_STUDENT_STATUS.TERMINATED;
  if (raw === "completed") return REPORT_STUDENT_STATUS.COMPLETED;
  if (raw === "active") return REPORT_STUDENT_STATUS.IN_PROGRESS;
  if (raw === "aborted") return REPORT_STUDENT_STATUS.ABORTED;
  if (raw === "absent") return REPORT_STUDENT_STATUS.ABSENT;
  return REPORT_STUDENT_STATUS.ASSIGNED;
};

/** Resolve admin report status label for a student row. */
export const resolveReportStudentDisplayStatus = (
  row: ReportStudentStatusInput,
  testEndTime?: string
): ReportStudentDisplayStatus => {
  const rawSession = row.session_status;
  if (rawSession != null && String(rawSession).trim() !== "") {
    return resolveLiveProctoringSessionStatus(rawSession);
  }

  if (
    row.display_status &&
    Object.values(REPORT_STUDENT_STATUS).includes(
      row.display_status as ReportStudentDisplayStatus
    )
  ) {
    return row.display_status as ReportStudentDisplayStatus;
  }

  const raw = (row.Test_status ?? "").trim();
  if (raw === ASSESSMENT_STATUS.VIOLATED) return REPORT_STUDENT_STATUS.TERMINATED;
  if (raw === ASSESSMENT_STATUS.ABORTED) return REPORT_STUDENT_STATUS.ABORTED;
  if (raw === ASSESSMENT_STATUS.COMPLETED) return REPORT_STUDENT_STATUS.COMPLETED;
  if (raw === ASSESSMENT_STATUS.STARTED || row.test_start_time) {
    return REPORT_STUDENT_STATUS.IN_PROGRESS;
  }

  if (testEndTime) {
    const endMs = Date.parse(testEndTime.replace(/\sIST$/i, ""));
    if (Number.isFinite(endMs) && Date.now() >= endMs) {
      return REPORT_STUDENT_STATUS.ABSENT;
    }
  }

  return REPORT_STUDENT_STATUS.ASSIGNED;
};

export const getReportStudentStatusStyle = (
  status: ReportStudentDisplayStatus
): { backgroundColor: string; color: string } => {
  switch (status) {
    case REPORT_STUDENT_STATUS.COMPLETED:
      return { backgroundColor: "#dcfce7", color: "#166534" };
    case REPORT_STUDENT_STATUS.IN_PROGRESS:
      return { backgroundColor: "#dbeafe", color: "#1d4ed8" };
    case REPORT_STUDENT_STATUS.ASSIGNED:
      return { backgroundColor: "#f1f5f9", color: "#475569" };
    case REPORT_STUDENT_STATUS.ABSENT:
      return { backgroundColor: "#fef3c7", color: "#92400e" };
    case REPORT_STUDENT_STATUS.ABORTED:
      return { backgroundColor: "#ffedd5", color: "#9a3412" };
    case REPORT_STUDENT_STATUS.TERMINATED:
      return { backgroundColor: "#fee2e2", color: "#991b1b" };
    default:
      return { backgroundColor: "#f1f5f9", color: "#475569" };
  }
};

/** Map proctored test-flow report page to legacy report detail shape */
export const mapProctoredReportToViewData = (
  data: Record<string, unknown>
): TestReportViewData => {
  const students = (data.students as Array<Record<string, unknown>>) ?? [];
  const firstMax = students.find((row) => row.max_marks != null)?.max_marks;
  const testEndTime = data.test_end_at ? String(data.test_end_at) : "";

  const rawControlStatus = String(data.exam_control_status ?? "none");
  const examControlStatus =
    rawControlStatus === "paused" || rawControlStatus === "aborted"
      ? rawControlStatus
      : "none";

  return {
    test_name: String(data.test_name ?? ""),
    course_name: String(data.course_name ?? ""),
    batch_name: String(data.batch_name ?? ""),
    batch_id: data.batch_id ? String(data.batch_id) : null,
    assigned_batches: (() => {
      const raw = data.assigned_batches;
      if (!Array.isArray(raw)) return [];
      return raw
        .map((row) => {
          const item = row as Record<string, unknown>;
          const id = String(item.batch_id ?? "").trim();
          if (!id) return null;
          return {
            batch_id: id,
            batch_name: String(item.batch_name ?? id),
          };
        })
        .filter(Boolean) as AssignedBatchOption[];
    })(),
    test_status: String(data.test_status ?? ""),
    exam_control_status: examControlStatus,
    test_start_time: data.test_start_at ? String(data.test_start_at) : "",
    test_end_time: data.test_end_at ? String(data.test_end_at) : "",
    duration: String(
      data.duration_minutes ?? data.default_duration_minutes ?? ""
    ),
    test_marks: Number(firstMax ?? 0),
    test_type: String(data.test_type ?? ""),
    auto_release_test_report: Boolean(data.auto_release_test_report),
    report_released: Boolean(data.report_released),
    report: students.map((row, index) => {
      const testStartTime = row.test_start_at ? String(row.test_start_at) : "";
      const testEndTimeRow = row.test_end_at ? String(row.test_end_at) : "";
      const sessionStatus =
        row.session_status != null ? String(row.session_status) : null;
      const display_status = resolveReportStudentDisplayStatus(
        {
          Test_status: String(row.assessment_status ?? ""),
          display_status: String(row.display_status ?? ""),
          session_status: sessionStatus,
          test_start_time: testStartTime,
          violation_count: Number(row.violation_count ?? 0),
        },
        testEndTime
      );

      return {
        ID: String(row.student_id ?? ""),
        USN: String(row.usn ?? row.USN ?? ""),
        Student: String(row.student_name ?? ""),
        College: String(row.college ?? ""),
        Branch: String(row.branch ?? ""),
        Category: String(row.category ?? ""),
        Rank: Number(row.row_number ?? index + 1),
        Date_Time: "",
        Max_marks:
          row.max_marks != null && row.max_marks !== undefined
            ? Number(row.max_marks)
            : 0,
        Obtained_marks:
          row.obtained_marks != null && row.obtained_marks !== undefined
            ? Number(row.obtained_marks)
            : 0,
        Percentage: Number(Number(row.score_percent ?? 0).toFixed(1)),
        final_percent:
          row.final_percent != null && row.final_percent !== undefined
            ? Number(row.final_percent)
            : null,
        score_retention_percent:
          row.score_retention_percent != null &&
          row.score_retention_percent !== undefined
            ? Number(row.score_retention_percent)
            : null,
        violation_penalty_percent:
          row.violation_penalty_percent != null &&
          row.violation_penalty_percent !== undefined
            ? Number(row.violation_penalty_percent)
            : null,
        violation_count: Number(row.violation_count ?? 0),
        extend_time_minutes: Number(
          row.extend_time_minutes ?? row.extend_time ?? 0
        ),
        extend_time_enabled: Boolean(row.extend_time_enabled),
        Test_status: String(row.assessment_status ?? ""),
        display_status,
        session_status: sessionStatus,
        test_start_time: testStartTime,
        test_end_time: testEndTimeRow,
      };
    }),
  };
};

/** Fetch tests for Configure tab filter sidebar */
export const fetchTestsList = async (
  filter: AssignTestsFilter
): Promise<TestData[]> => {
  const response = await authService.post(
    `${TEST_FLOW_API}/tests/for-assign`,
    {
      track: filter.track || null,
      subject: filter.subject || null,
      topic: filter.topic || null,
    }
  );
  const raw = response.data;
  const list = Array.isArray(raw) ? raw : [];
  return list.map((row) =>
    mapTestDataFromApi(row as Record<string, unknown>)
  );
};

export type CreateableTestTypeItem = {
  test_type: string;
  prefix: string;
};

/** Create-flow test types + name prefixes from backend */
export const fetchCreateableTestTypes = async (): Promise<
  CreateableTestTypeItem[]
> => {
  const response = await authService.get("/admin/tests/createable-types");
  const rows = response.data?.test_types;
  if (!Array.isArray(rows)) return [];
  return rows.map((row: { test_type: string; prefix: string }) => ({
    test_type: String(row.test_type),
    prefix: String(row.prefix),
  }));
};

/** Next auto-name sequence for (test_type + course set) */
export const fetchNextTestNameSequence = async (
  testType: string,
  courseIds: string[]
): Promise<number> => {
  const response = await authService.post("/admin/tests/next-name-sequence", {
    test_type: testType,
    course_ids: courseIds,
  });
  return Number(response.data?.sequence) || 1;
};

/** Load subjects stored on a test (master test table) */
export const fetchTestSubjects = async (
  testId: string
): Promise<{
  subjects: SelectedSubject[];
  track_id: string | null;
}> => {
  const response = await authService.get(
    `${TEST_FLOW_API}/tests/${testId}/subjects`
  );
  const data = response.data ?? {};
  const subjects = Array.isArray(data.subjects)
    ? data.subjects.map((row: { subject_id: string; subject_name: string }) => ({
        subject_id: String(row.subject_id),
        subject_name: String(row.subject_name),
      }))
    : [];
  return {
    subjects,
    track_id: data.track_id ? String(data.track_id) : null,
  };
};

/** Allowed courses for configure/assign (from test_courses) */
export const fetchAllowedCourses = async (
  testId: string
): Promise<{
  track_id: string | null;
  courses: { course_id: string; course_name: string }[];
}> => {
  const response = await authService.get(
    `${TEST_FLOW_API}/tests/${testId}/allowed-courses`
  );
  const data = response.data ?? {};
  const courses = Array.isArray(data.courses)
    ? data.courses.map(
        (row: { course_id: string; course_name: string }) => ({
          course_id: String(row.course_id),
          course_name: String(row.course_name),
        })
      )
    : [];
  return {
    track_id: data.track_id ? String(data.track_id) : null,
    courses,
  };
};

/** Persist selected subjects on a test (master test table) */
export const saveTestSubjects = async (
  testId: string,
  subjectIds: string[]
): Promise<{
  subjects: SelectedSubject[];
  track_id: string | null;
}> => {
  const response = await authService.put(
    `${TEST_FLOW_API}/tests/${testId}/subjects`,
    { subject_ids: subjectIds }
  );
  const data = response.data ?? {};
  const subjects = Array.isArray(data.subjects)
    ? data.subjects.map((row: { subject_id: string; subject_name: string }) => ({
        subject_id: String(row.subject_id),
        subject_name: String(row.subject_name),
      }))
    : [];
  return {
    subjects,
    track_id: data.track_id ? String(data.track_id) : null,
  };
};

/** Batch-level assignment rows for a test */
export const fetchTestAssignments = async (
  testId: string
): Promise<TestFlowAssignmentItem[]> => {
  const response = await authService.get(
    `${TEST_FLOW_API}/tests/${testId}/assignments`
  );
  return response.data?.assignments ?? [];
};

/** Soft-delete a batch assignment for a test */
export const removeTestBatchAssignment = async (
  testId: string,
  batchId: string
): Promise<void> => {
  await authService.delete(
    `${TEST_FLOW_API}/tests/${testId}/assignments/${batchId}`
  );
};

const toDateInputValue = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString().slice(0, 10);
};

const toTimeInputValue = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toTimeString().slice(0, 5);
};

const buildBatchConfigEntry = async (
  testId: string,
  assignment: TestFlowAssignmentItem,
  batchStudents: Array<{
    student_id: string;
    course_id?: string;
    course?: string;
    batch?: string;
  }>,
  subjectNames: string[],
  test: TestData
): Promise<BatchConfigEntry> => {
  const firstStudent = batchStudents[0];
  let securityConfig = { ...DEFAULT_TEST_SECURITY_CONFIGURATION };
  let securitySaved = false;

  try {
    const security = await fetchTestSecurityConfiguration(
      testId,
      assignment.batch_id
    );
    if (security.status !== "not_found") {
      securityConfig = pickTestSecurityConfigurationPayload(security);
      securitySaved = true;
    }
  } catch (error) {
    console.error(
      `Failed to load security config for batch ${assignment.batch_id}:`,
      error
    );
  }

  const scheduleSaved = Boolean(assignment.start_time && assignment.end_time);
  const status: BatchConfigEntry["status"] = scheduleSaved
    ? "scheduled"
    : batchStudents.length > 0
      ? "partial"
      : "draft";

  return {
    batch_id: assignment.batch_id,
    batch_name: assignment.batch_name ?? assignment.batch_id,
    course_id: firstStudent?.course_id ?? test.course_id ?? "",
    course_name: firstStudent?.course ?? "",
    track_id: test.track_id ?? "",
    track_name: "",
    subjects: subjectNames,
    test_type: test.test_type,
    date: toDateInputValue(assignment.start_time),
    time: toTimeInputValue(assignment.start_time),
    end_date: toDateInputValue(assignment.end_time),
    end_time: toTimeInputValue(assignment.end_time),
    securityConfig,
    status,
    assignedStudentIds: batchStudents.map((row) => row.student_id),
    slot_assigned_counts: {},
    securitySaved,
    scheduleSaved,
  };
};

/** Hydrate configure-detail batch cards from test-flow assignment APIs */
export const loadBatchConfigsFromBackend = async (
  testId: string,
  subjects: SelectedSubject[],
  test: TestData
): Promise<BatchConfigEntry[]> => {
  const [assignments, assignedResponse, slotsResponse] = await Promise.all([
    fetchTestAssignments(testId),
    fetchAssignedStudents(testId),
    authService
      .get(`${TEST_FLOW_API}/tests/${testId}/slots`)
      .catch(() => ({ data: { slots: [] } })),
  ]);

  const subjectNames = subjects.map((row) => row.subject_name);
  const studentsByBatch = new Map<
    string,
    Array<{ student_id: string; course_id?: string; course?: string }>
  >();

  for (const student of assignedResponse?.students ?? []) {
    if (!student.batch_id) continue;
    const existing = studentsByBatch.get(student.batch_id) ?? [];
    existing.push(student);
    studentsByBatch.set(student.batch_id, existing);
  }

  let assignmentRows = assignments;
  if (!assignmentRows.length && studentsByBatch.size > 0) {
    const syntheticRows: TestFlowAssignmentItem[] = [];
    studentsByBatch.forEach((_, batchId) => {
      syntheticRows.push({
        assignment_id: 0,
        batch_id: batchId,
        batch_name: batchId,
        start_time: test.date && test.time ? `${test.date}T${test.time}` : null,
        end_time: null,
        duration_minutes: parseInt(test.duration, 10) || 0,
        status: "draft",
      });
    });
    assignmentRows = syntheticRows;
  } else if (!assignmentRows.length && test.batch_id) {
    assignmentRows = [
      {
        assignment_id: 0,
        batch_id: test.batch_id,
        batch_name: test.batch ?? test.batch_id,
        start_time: test.date && test.time ? `${test.date}T${test.time}` : null,
        end_time: null,
        duration_minutes: parseInt(test.duration, 10) || 0,
        status: test.date && test.time ? "scheduled" : "draft",
      },
    ];
  }

  // Slot-linked batches may exist without a test_assignments row yet
  const slotBatches = (slotsResponse?.data?.slots ?? []) as Array<{
    slot_id?: number;
    batches?: Array<{
      batch_id: string;
      batch_name?: string | null;
      course_id?: string | null;
      course_name?: string | null;
      assigned_student_count?: number;
    }>;
  }>;
  const slotCountsByBatch = new Map<string, Record<string, number>>();
  for (const slot of slotBatches) {
    if (slot.slot_id == null) continue;
    const slotKey = String(slot.slot_id);
    for (const batch of slot.batches || []) {
      if (!batch.batch_id) continue;
      const counts = slotCountsByBatch.get(batch.batch_id) ?? {};
      counts[slotKey] = Number(batch.assigned_student_count ?? 0);
      slotCountsByBatch.set(batch.batch_id, counts);
    }
  }
  const knownBatchIds = new Set(assignmentRows.map((row) => row.batch_id));
  for (const slot of slotBatches) {
    for (const batch of slot.batches || []) {
      if (!batch.batch_id || knownBatchIds.has(batch.batch_id)) continue;
      knownBatchIds.add(batch.batch_id);
      assignmentRows = [
        ...assignmentRows,
        {
          assignment_id: 0,
          batch_id: batch.batch_id,
          batch_name: batch.batch_name || batch.batch_id,
          start_time: null,
          end_time: null,
          duration_minutes: 0,
          status: "draft",
        },
      ];
      // Seed course from slot batch metadata when no students yet
      if (!studentsByBatch.has(batch.batch_id) && batch.course_id) {
        studentsByBatch.set(batch.batch_id, [
          {
            student_id: "",
            course_id: String(batch.course_id),
            course: batch.course_name || "",
          },
        ]);
      }
    }
  }

  // Drop the empty student_id seed before build (assignedStudentIds)
  for (const [batchId, rows] of [...studentsByBatch.entries()]) {
    const cleaned = rows.filter((r) => r.student_id);
    if (cleaned.length === 0 && rows.length > 0) {
      // keep course metadata via synthetic student stripped in build — use first for course only
      studentsByBatch.set(batchId, rows);
    } else {
      studentsByBatch.set(batchId, cleaned.length ? cleaned : rows);
    }
  }

  if (!assignmentRows.length) {
    return [];
  }

  const entries = await Promise.all(
    assignmentRows.map(async (assignment) => {
      const batchStudents = (studentsByBatch.get(assignment.batch_id) ?? []).filter(
        (row) => row.student_id
      );
      const courseSeed = (studentsByBatch.get(assignment.batch_id) ?? [])[0];
      const entry = await buildBatchConfigEntry(
        testId,
        assignment,
        batchStudents.length
          ? batchStudents
          : courseSeed
            ? [
                {
                  student_id: "__seed__",
                  course_id: courseSeed.course_id,
                  course: courseSeed.course,
                },
              ]
            : [],
        subjectNames,
        test
      );
      if (entry.assignedStudentIds.includes("__seed__")) {
        entry.assignedStudentIds = [];
        entry.status = "draft";
      }
      // Prefer slot batch names/courses when assignment row is synthetic
      const slotMeta = slotBatches
        .flatMap((s) => s.batches || [])
        .find((b) => b.batch_id === assignment.batch_id);
      if (slotMeta) {
        entry.batch_name = slotMeta.batch_name || entry.batch_name;
        if (slotMeta.course_id) entry.course_id = String(slotMeta.course_id);
        if (slotMeta.course_name) entry.course_name = slotMeta.course_name;
      }
      entry.slot_assigned_counts =
        slotCountsByBatch.get(assignment.batch_id) ?? {};
      return entry;
    })
  );
  return entries;
};

/** Fetch report summary cards (assigned date descending) */
export const fetchReportsList = async (
  filter: ReportTestsFilter
): Promise<ReportData[]> => {
  const response = await authService.post(
    `${TEST_FLOW_API}/tests/report-list`,
    filter
  );
  const raw = response.data;
  const list = Array.isArray(raw) ? raw : [];
  return sortReportsByAssignedDateDesc(
    list.map((row) => mapReportDataFromApi(row as Record<string, unknown>))
  );
};

export type ReportStudentActionPayload = {
  student_id: string;
  extend_time_minutes: number;
  extend_time_enabled: boolean;
};

export type ExamControlAction = "pause" | "resume" | "abort";

export type ExamControlOptions = {
  slot_ids?: number[];
  all_slots?: boolean;
};

/** Pause/resume (non-FT) or abort Final/OFT slot(s) */
export const applyExamControl = async (
  testId: string,
  action: ExamControlAction,
  options?: ExamControlOptions
): Promise<{
  exam_control_status: string;
  message: string;
  sessions_updated?: number;
  logs_recorded?: number;
  slots_aborted?: number[];
  students_removed?: number;
}> => {
  const response = await authService.post(
    `${TEST_FLOW_API}/tests/${testId}/report/exam-control`,
    {
      action,
      slot_ids: options?.slot_ids,
      all_slots: Boolean(options?.all_slots),
    }
  );
  return response.data;
};

/** Save extend-time provisions from the report grid */
export const saveReportStudentActions = async (
  testId: string,
  studentActions: ReportStudentActionPayload[]
): Promise<void> => {
  await authService.post(
    `${TEST_FLOW_API}/tests/${testId}/report/student-actions`,
    { student_actions: studentActions }
  );
};

/** Fetch per-student results for one test; stores TestID in sessionStorage */
export const fetchTestReportDetail = async (
  testId: string,
  options?: {
    live?: boolean;
    batchId?: string | null;
    slotId?: number | string | null;
  }
): Promise<TestReportViewData> => {
  const params = new URLSearchParams();
  if (options?.live) params.set("live", "true");
  if (options?.batchId) params.set("batch_id", options.batchId);
  if (options?.slotId != null && String(options.slotId).trim() !== "") {
    params.set("slot_id", String(options.slotId));
  }
  const query = params.toString() ? `?${params.toString()}` : "";
  const response = await authService.get(
    `${TEST_FLOW_API}/tests/${testId}/report${query}`
  );
  sessionStorage.setItem("TestID", testId);
  return mapProctoredReportToViewData(response.data);
};

/** Students available for assignment in a course/batch */
export const fetchStudentsByCourseBatch = async (
  course: string,
  batch: string,
  testId: string
) => {
  const encodedBatch = encodeURIComponent(batch);
  const encodedTestId = encodeURIComponent(testId);
  const url = course
    ? `${TEST_FLOW_API}/filters/assign/${encodeURIComponent(course)}/${encodedBatch}/${encodedTestId}`
    : `${TEST_FLOW_API}/filters/assign/${encodedBatch}/${encodedTestId}`;
  const response = await authService.get(url);
  return response.data;
};

/** Already-assigned students for a test */
export const fetchAssignedStudents = async (testId: string) => {
  const response = await authService.get(
    `${TEST_FLOW_API}/tests/${testId}/assigned-students`
  );
  return response.data;
};

/** Assign students to a test (no schedule). Pass slot_id for Final/Overall Final multi-slot. */
export const assignTestStudents = async (
  testId: string,
  studentIds: string[],
  batchId?: string,
  slotId?: number | null
): Promise<void> => {
  await authService.post(`${TEST_FLOW_API}/tests/${testId}/assign`, {
    students_list: studentIds,
    batch_id: batchId ?? null,
    ...(slotId != null ? { slot_id: slotId } : {}),
  });
};

/** Soft-delete previous same-subject Final Test assessment so student can be assigned here. */
export const clearSubjectFinalAssessment = async (
  currentTestId: string,
  studentId: string,
  previousTestId: string
): Promise<void> => {
  await authService.post(
    `${TEST_FLOW_API}/tests/${currentTestId}/clear-subject-final-assessment`,
    {
      student_id: studentId,
      previous_test_id: previousTestId,
    }
  );
};

/** Schedule assigned students via test-flow API */
export const scheduleTestStudents = async (
  testId: string,
  studentIds: string[],
  test: TestData
): Promise<void> => {
  if (!test.date || !test.time) {
    throw new Error("Date and time are required to schedule the test");
  }

  const isPracticeTest = test.test_type === "Practice Test";
  const testStartTime = new Date(`${test.date}T${test.time}:00`);

  let testEndTime: Date;
  let durationMinutes: number;

  durationMinutes = parseInt(test.duration, 10) || 60;

  if (isPracticeTest) {
    if (!test.end_date || !test.end_time) {
      throw new Error(
        "End date and end time are required to schedule a Practice Test"
      );
    }
    testEndTime = new Date(`${test.end_date}T${test.end_time}:00`);
    if (
      Number.isNaN(testStartTime.getTime()) ||
      Number.isNaN(testEndTime.getTime())
    ) {
      throw new Error("Invalid start or end date/time for Practice Test");
    }
    if (testEndTime <= testStartTime) {
      throw new Error("Start date/time must be before end date/time");
    }
  } else {
    testEndTime = new Date(
      testStartTime.getTime() + durationMinutes * 60 * 1000
    );
  }

  await authService.post(`${TEST_FLOW_API}/tests/${testId}/schedule`, {
    students_list: studentIds,
    test_start_time: testStartTime.toISOString(),
    test_end_time: testEndTime.toISOString(),
    duration_minutes: durationMinutes,
    batch_id: test.batch_id ?? undefined,
  });
};

/** Individual student report (MCQ/coding breakdown) */
export const fetchStudentTestReport = async (
  testId: string,
  studentId: string
) => {
  const response = await authService.get(
    `${TEST_FLOW_API}/tests/${testId}/report/students/${studentId}`
  );
  return response.data;
};

/** Persist batch/track/course assignment without requiring a schedule */
export const saveTestBatchAssignment = async (
  test: TestData,
  batch: {
    track_id: string;
    course_id: string;
    batch_id: string;
    test_type?: string | null;
    subjects?: string[];
  }
): Promise<void> => {
  const resolvedTestId = test.test_id;
  if (!resolvedTestId) {
    throw new Error("Missing test id");
  }
  await authService.post("/admin/test/assign/update/", {
    test_id: resolvedTestId,
    test_type: batch.test_type ?? test.test_type ?? "",
    track: batch.track_id,
    course: batch.course_id,
    subject: batch.subjects?.[0] ?? test.subject ?? "",
    batch_id: batch.batch_id,
    date: "",
    time: "",
  });
};

/** Persist test metadata from assign/configure flows */
export const updateTestMetadata = async (
  test: TestData,
  editingTest: TestData,
  options?: { assignedStudentIds?: string[] }
): Promise<void> => {
  const resolvedTestId = test.test_id || editingTest.test_id || "";
  if (!resolvedTestId) {
    throw new Error("Missing test id");
  }
  await authService.post("/admin/test/assign/update/", {
    test_id: resolvedTestId,
    test_type: test.test_type ?? editingTest.test_type ?? "",
    track: test.track_id ?? editingTest.track_id ?? "",
    course: test.course_id ?? editingTest.course_id ?? "",
    subject: test.subject ?? editingTest.subject ?? "",
    batch_id: test.batch_id ?? "",
    date: test.date ?? "",
    time: test.time ?? "",
  });

  if (test.date && test.time) {
    await scheduleTestStudents(
      resolvedTestId,
      options?.assignedStudentIds ?? [],
      test
    );
  }
};

export type ReportDetailStats = {
  totalStudents: number;
  appeared: number;
  cleared: number;
  failed: number;
  avgScore: number;
};

const CLEAR_CUTOFF_PERCENT = 40;

/**
 * Summary counts for the report detail stats row.
 * - totalStudents: all students assigned to the test
 * - appeared: students whose status is neither "Assigned" nor "Absent"
 * - cleared: students with a final result scoring >= 40%
 * - failed: students with a final result scoring < 40%.
 *   Once the test is over, absent students are also counted as failed.
 * - avgScore: mean score percentage across students with a final result
 */
export const computeReportDetailStats = (
  rows: ReportStudentRow[] | undefined,
  testEndTime?: string,
  testOver = false
): ReportDetailStats => {
  if (!rows?.length) {
    return { totalStudents: 0, appeared: 0, cleared: 0, failed: 0, avgScore: 0 };
  }

  let appeared = 0;
  let cleared = 0;
  let failed = 0;
  const percentages: number[] = [];

  for (const row of rows) {
    const displayStatus = resolveReportStudentDisplayStatus(row, testEndTime);

    const notAppeared =
      displayStatus === REPORT_STUDENT_STATUS.ASSIGNED ||
      displayStatus === REPORT_STUDENT_STATUS.ABSENT;
    if (!notAppeared) {
      appeared += 1;
    }

    // Once the test is over, absent students count as failed.
    if (testOver && displayStatus === REPORT_STUDENT_STATUS.ABSENT) {
      failed += 1;
      continue;
    }

    const hasFinalResult =
      displayStatus === REPORT_STUDENT_STATUS.COMPLETED ||
      displayStatus === REPORT_STUDENT_STATUS.TERMINATED ||
      displayStatus === REPORT_STUDENT_STATUS.ABORTED;
    const hasPercentage =
      row.Percentage !== null &&
      row.Percentage !== undefined &&
      !Number.isNaN(row.Percentage);

    if (hasFinalResult && hasPercentage) {
      if (row.Percentage >= CLEAR_CUTOFF_PERCENT) {
        cleared += 1;
      } else {
        failed += 1;
      }
      percentages.push(row.Percentage);
    }
  }

  const avgScore = percentages.length
    ? percentages.reduce((sum, value) => sum + value, 0) / percentages.length
    : 0;

  return {
    totalStudents: rows.length,
    appeared,
    cleared,
    failed,
    avgScore,
  };
};

export const getReportFinalScoreMarks = (
  student: ReportStudentRow
): number | null => {
  if (
    student.Obtained_marks === null ||
    student.Obtained_marks === undefined
  ) {
    return null;
  }
  const integrity = student.score_retention_percent ?? 100;
  return Number(
    ((student.Obtained_marks * integrity) / 100).toFixed(2)
  );
};

export const getReportFinalScorePercent = (
  student: ReportStudentRow
): number | null => {
  if (student.Percentage === null || student.Percentage === undefined) {
    return null;
  }
  const integrity = student.score_retention_percent ?? 100;
  return Number(((student.Percentage * integrity) / 100).toFixed(1));
};

export const isReportStudentAssigned = (student: ReportStudentRow): boolean =>
  student.display_status === REPORT_STUDENT_STATUS.ASSIGNED;

/** Format a raw score percentage for display (1 decimal place). */
export const formatReportPercentageDisplay = (percent: number): string =>
  `${Number(percent.toFixed(1))}%`;

export const formatObtainedValue = (student: ReportStudentRow): string => {
  if (isReportStudentAssigned(student)) return "--";

  const hasObtained =
    student.Obtained_marks !== null && student.Obtained_marks !== undefined;
  const hasPercentage =
    student.Percentage !== null && student.Percentage !== undefined;

  if (!hasObtained && !hasPercentage) return "--";
  if (hasObtained && hasPercentage) {
    return `${student.Obtained_marks} (${formatReportPercentageDisplay(student.Percentage)})`;
  }
  if (hasObtained) return String(student.Obtained_marks);
  return `(${formatReportPercentageDisplay(student.Percentage)})`;
};

export const formatFinalScoreValue = (student: ReportStudentRow): string => {
  if (isReportStudentAssigned(student)) return "--";

  const finalMarks = getReportFinalScoreMarks(student);
  const finalPercent = getReportFinalScorePercent(student);
  const hasMarks = finalMarks !== null && finalMarks !== undefined;
  const hasPercent = finalPercent !== null && finalPercent !== undefined;

  if (!hasMarks && !hasPercent) return "--";
  if (hasMarks && hasPercent) {
    return `${finalMarks} (${finalPercent}%)`;
  }
  if (hasMarks) return String(finalMarks);
  return `(${finalPercent}%)`;
};

export type ReportStudentSortField =
  | "ID"
  | "USN"
  | "Student"
  | "College"
  | "Branch"
  | "Category"
  | "display_status"
  | "Max_marks"
  | "Obtained_marks"
  | "Percentage"
  | "final_percent"
  | "violation_count"
  | "Rank";

/** Sort student rows for the report detail table */
export const sortReportDetailRows = (
  rows: ReportStudentRow[],
  field: ReportStudentSortField,
  direction: "asc" | "desc"
): ReportStudentRow[] => {
  return [...rows].sort((left, right) => {
    if (field === "final_percent") {
      const leftValue = getReportFinalScorePercent(left) ?? -1;
      const rightValue = getReportFinalScorePercent(right) ?? -1;
      return compareNumericScore(leftValue, rightValue, direction);
    }

    const sortField = field === "Obtained_marks" ? "Percentage" : field;
    const leftValue = left[sortField as keyof ReportStudentRow];
    const rightValue = right[sortField as keyof ReportStudentRow];

    if (
      isScoreSortField(String(sortField)) ||
      sortField === "Max_marks" ||
      sortField === "Rank" ||
      sortField === "violation_count"
    ) {
      return compareNumericScore(leftValue, rightValue, direction);
    }

    return compareMaybeNumeric(leftValue, rightValue, direction);
  });
};

/** Filter rows in report detail table by search query */
export const filterReportDetailRows = (
  rows: ReportStudentRow[] | undefined,
  searchQuery: string
): ReportStudentRow[] => {
  if (!rows) return [];
  const q = searchQuery.toLowerCase();
  return rows.filter(
    (student) =>
      student.ID.toLowerCase().includes(q) ||
      (student.USN || "").toLowerCase().includes(q) ||
      student.Student.toLowerCase().includes(q) ||
      student.College.toLowerCase().includes(q) ||
      student.Branch.toLowerCase().includes(q) ||
      student.Category.toLowerCase().includes(q)
  );
};

/** Export filtered report detail as CSV */
export const downloadTestReportCsv = (
  rows: ReportStudentRow[],
  testReportView: TestReportViewData
) => {
  if (!rows.length) return;

  const headers = [
    "ID",
    "USN",
    "Student",
    "College",
    "Branch",
    "Category",
    "Status",
    "Max Marks",
    "Obtained",
    "Final Score",
    "No of violations",
  ];

  const csvContent = [
    headers.join(","),
    ...rows.map((student) => {
      const row = [
        student.ID,
        student.USN,
        student.Student,
        student.College,
        student.Branch,
        student.Category,
        resolveReportStudentDisplayStatus(
          student,
          testReportView.test_end_time
        ),
      ];

      row.push(
        String(
          student.Max_marks !== null && student.Max_marks !== undefined
            ? student.Max_marks
            : "--"
        ),
        formatObtainedValue(student),
        formatFinalScoreValue(student),
        String(student.violation_count ?? 0)
      );

      return row.join(",");
    }),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  const url = URL.createObjectURL(blob);
  link.setAttribute("href", url);
  link.setAttribute(
    "download",
    `test_report_${testReportView.test_name || "unknown"}_${
      new Date().toISOString().split("T")[0]
    }.csv`
  );
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
